import React from 'react';
import { Link } from 'react-router-dom';

function WorkbookPreview() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-cream-50 to-white">
      {/* Header */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <Link to="/workbook" className="inline-flex items-center text-cream-700 hover:text-cream-800 mb-8 transition-colors">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Workbook
          </Link>
          
          <h1 className="text-5xl md:text-6xl font-light text-charcoal mb-6">
            Inside the Workbook
          </h1>
          <p className="text-xl text-charcoal/70 leading-relaxed">
            See what you'll get with the Boundary Reset Workbook. Every page is designed to help you build confidence, overcome guilt, and create boundaries that last.
          </p>
        </div>
      </section>

      {/* Preview Content */}
      <section className="px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          {/* Module 1 */}
          <div className="mb-16 bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-cream-600 p-6">
              <h2 className="text-3xl font-light text-white">Module 1: Understanding Your Boundaries</h2>
            </div>
            <div className="p-8">
              <p className="text-lg text-charcoal/80 mb-6">
                Before you can set healthy boundaries, you need to understand where yours currently stand. This module helps you identify:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-cream-50 p-6 rounded-xl">
                  <h3 className="text-xl font-medium text-charcoal mb-3">📊 Boundary Assessment</h3>
                  <p className="text-charcoal/70">
                    Rate your boundaries in 8 key life areas: work, family, friendships, romantic relationships, self-care, finances, time, and emotional energy.
                  </p>
                </div>
                
                <div className="bg-cream-50 p-6 rounded-xl">
                  <h3 className="text-xl font-medium text-charcoal mb-3">🔍 Pattern Recognition</h3>
                  <p className="text-charcoal/70">
                    Identify recurring situations where you say yes when you mean no, and understand the triggers that make you abandon your boundaries.
                  </p>
                </div>
                
                <div className="bg-cream-50 p-6 rounded-xl">
                  <h3 className="text-xl font-medium text-charcoal mb-3">💭 Belief Exploration</h3>
                  <p className="text-charcoal/70">
                    Uncover the beliefs that keep you stuck in people-pleasing mode, like "I'm selfish if I say no" or "I'll lose relationships if I set boundaries."
                  </p>
                </div>
                
                <div className="bg-cream-50 p-6 rounded-xl">
                  <h3 className="text-xl font-medium text-charcoal mb-3">🎯 Goal Setting</h3>
                  <p className="text-charcoal/70">
                    Define what healthy boundaries would look like for you in each area of your life, creating a clear vision to work toward.
                  </p>
                </div>
              </div>
              
              <div className="bg-cream-100 border-l-4 border-cream-600 p-4">
                <p className="text-sm text-charcoal/70 italic">
                  <strong>Sample Exercise:</strong> "In the past week, write down 3 times you said yes when you wanted to say no. What made you say yes? What were you afraid would happen if you said no?"
                </p>
              </div>
            </div>
          </div>

          {/* Module 2 */}
          <div className="mb-16 bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-cream-600 p-6">
              <h2 className="text-3xl font-light text-white">Module 2: Working Through Guilt & Fear</h2>
            </div>
            <div className="p-8">
              <p className="text-lg text-charcoal/80 mb-6">
                Guilt is the biggest barrier to setting boundaries. This module teaches you how to acknowledge guilt without letting it control you.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-cream-50 p-6 rounded-xl">
                  <h3 className="text-xl font-medium text-charcoal mb-3">😰 Guilt Mapping</h3>
                  <p className="text-charcoal/70">
                    Understand where your guilt comes from and why it shows up when you try to prioritize yourself. Learn the difference between healthy and unhealthy guilt.
                  </p>
                </div>
                
                <div className="bg-cream-50 p-6 rounded-xl">
                  <h3 className="text-xl font-medium text-charcoal mb-3">🧠 Thought Reframing</h3>
                  <p className="text-charcoal/70">
                    Challenge unhelpful thoughts like "I'm being selfish" and replace them with more balanced, compassionate perspectives.
                  </p>
                </div>
                
                <div className="bg-cream-50 p-6 rounded-xl">
                  <h3 className="text-xl font-medium text-charcoal mb-3">💪 Building Tolerance</h3>
                  <p className="text-charcoal/70">
                    Practice sitting with discomfort through guided exercises. Learn that you can handle guilt without giving in to it.
                  </p>
                </div>
                
                <div className="bg-cream-50 p-6 rounded-xl">
                  <h3 className="text-xl font-medium text-charcoal mb-3">🛡️ Self-Compassion Practice</h3>
                  <p className="text-charcoal/70">
                    Develop a kinder inner voice that supports your boundary-setting instead of criticizing you for it.
                  </p>
                </div>
              </div>
              
              <div className="bg-cream-100 border-l-4 border-cream-600 p-4">
                <p className="text-sm text-charcoal/70 italic">
                  <strong>Sample Exercise:</strong> "Write a letter to yourself from the perspective of a compassionate friend explaining why it's okay to set boundaries, even when it feels hard."
                </p>
              </div>
            </div>
          </div>

          {/* Module 3 */}
          <div className="mb-16 bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-cream-600 p-6">
              <h2 className="text-3xl font-light text-white">Module 3: Practical Boundary Scripts</h2>
            </div>
            <div className="p-8">
              <p className="text-lg text-charcoal/80 mb-6">
                Knowing what to say is half the battle. This module gives you ready-to-use scripts for common boundary-setting scenarios.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-cream-50 p-6 rounded-xl">
                  <h3 className="text-xl font-medium text-charcoal mb-3">💼 Work Boundaries</h3>
                  <p className="text-charcoal/70">
                    Scripts for saying no to extra projects, leaving work on time, and protecting your off-hours from work requests.
                  </p>
                </div>
                
                <div className="bg-cream-50 p-6 rounded-xl">
                  <h3 className="text-xl font-medium text-charcoal mb-3">👨‍👩‍👧‍👦 Family Boundaries</h3>
                  <p className="text-charcoal/70">
                    Navigate difficult family dynamics with scripts for declining invitations, limiting contact, and responding to guilt trips.
                  </p>
                </div>
                
                <div className="bg-cream-50 p-6 rounded-xl">
                  <h3 className="text-xl font-medium text-charcoal mb-3">👥 Friend Boundaries</h3>
                  <p className="text-charcoal/70">
                    Set limits on emotional labor, decline social plans without losing friendships, and ask for reciprocity.
                  </p>
                </div>
                
                <div className="bg-cream-50 p-6 rounded-xl">
                  <h3 className="text-xl font-medium text-charcoal mb-3">💑 Relationship Boundaries</h3>
                  <p className="text-charcoal/70">
                    Communicate needs, request space, and maintain your identity within a romantic relationship.
                  </p>
                </div>
              </div>
              
              <div className="bg-cream-100 border-l-4 border-cream-600 p-4">
                <p className="text-sm text-charcoal/70 italic">
                  <strong>Sample Script:</strong> "I appreciate you thinking of me, but I can't take that on right now. I hope you understand." [No justification needed!]
                </p>
              </div>
            </div>
          </div>

          {/* Module 4 */}
          <div className="mb-16 bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-cream-600 p-6">
              <h2 className="text-3xl font-light text-white">Module 4: Tracking Your Progress</h2>
            </div>
            <div className="p-8">
              <p className="text-lg text-charcoal/80 mb-6">
                Change happens gradually. This module helps you celebrate wins, learn from challenges, and stay motivated on your boundary-setting journey.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-cream-50 p-6 rounded-xl">
                  <h3 className="text-xl font-medium text-charcoal mb-3">📈 Weekly Check-Ins</h3>
                  <p className="text-charcoal/70">
                    Track your boundary wins, challenges faced, and lessons learned each week to see your progress over time.
                  </p>
                </div>
                
                <div className="bg-cream-50 p-6 rounded-xl">
                  <h3 className="text-xl font-medium text-charcoal mb-3">🎉 Wins Tracker</h3>
                  <p className="text-charcoal/70">
                    Celebrate every boundary you set, no matter how small. Build evidence that you're capable of change.
                  </p>
                </div>
                
                <div className="bg-cream-50 p-6 rounded-xl">
                  <h3 className="text-xl font-medium text-charcoal mb-3">🔄 Reflection Prompts</h3>
                  <p className="text-charcoal/70">
                    Process your experiences through guided journaling prompts designed to deepen your self-awareness.
                  </p>
                </div>
                
                <div className="bg-cream-50 p-6 rounded-xl">
                  <h3 className="text-xl font-medium text-charcoal mb-3">🗓️ 30-Day Challenge</h3>
                  <p className="text-charcoal/70">
                    Set one boundary every day for 30 days with suggested challenges to gradually build your confidence.
                  </p>
                </div>
              </div>
              
              <div className="bg-cream-100 border-l-4 border-cream-600 p-4">
                <p className="text-sm text-charcoal/70 italic">
                  <strong>Sample Prompt:</strong> "What's one small boundary I can set this week? How will I know I've succeeded? How will I celebrate?"
                </p>
              </div>
            </div>
          </div>

          {/* Bonus Section */}
          <div className="bg-gradient-to-br from-cream-600 to-cream-700 text-white rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl md:text-4xl font-light mb-6">🎁 Bonus Resources Included</h2>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <svg className="w-6 h-6 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-lg">Quick Reference Guide: 50+ boundary scripts for any situation</span>
              </li>
              <li className="flex items-start gap-3">
                <svg className="w-6 h-6 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-lg">Printable boundary-setting affirmation cards</span>
              </li>
              <li className="flex items-start gap-3">
                <svg className="w-6 h-6 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-lg">Guided meditation for releasing guilt (audio file)</span>
              </li>
              <li className="flex items-start gap-3">
                <svg className="w-6 h-6 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-lg">Emergency boundary toolkit for difficult conversations</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 py-20 bg-cream-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-light text-charcoal mb-6">
            Ready to Start Your Boundary Journey?
          </h2>
          <p className="text-xl text-charcoal/70 mb-8">
            Get instant access to all 4 modules plus bonus resources for just $29.
          </p>
          <Link 
            to="/workbook/get-started"
            className="inline-block bg-cream-600 text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-cream-700 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            Get the Workbook Now
          </Link>
          <p className="text-sm text-charcoal/60 mt-4">
            Instant download • Lifetime access • Free updates
          </p>
        </div>
      </section>
    </div>
  );
}

export default WorkbookPreview;
